import "./FooterComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, IRootElementService, IScope} from "angular";
import {OrderViewStore} from "../../orders/store/view/OrderViewStore";
import {OrderEventStore} from "../../orders/store/event/OrderEventStore";
import {EquityTicketService} from "../../portfolio/templates/ticket/equity-ticket/EquityTicketService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {FeatureNames} from "../../types/UserConfig";
import {FooterOrder, FooterOrders} from "./models/FooterOrder";
import {EquityOrderViewModel, OrderViewModels, WarrantOrderViewModel} from "../../orders/models/OrderViewModel";
import {isNotNull} from "../../js/util/UtilFunctions";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {SystemEvent} from "../../services/system-state/SystemEvent";
import {isIressOrderModel} from "../../orders/models/iress/IressOrderViewModel";
import {isSimulatedOrderModel} from "app/orders/models/simulated/SimulatedOrderViewModel";
import {Branding} from "../../types/Branding";
import {MultiAccountEquityTicketService} from "../../portfolio/templates/ticket/multi-equity-ticket/MultiAccountEquityTicketService";

export const Footer = "tfFooter";

export function footer(parentDir: string): IComponentOptions {
    return {
        templateUrl: `${parentDir}/footer/FooterComponent.html`,
        controllerAs: "ctrl",
        controller: FooterComponentController
    };
}

export class FooterComponentController implements IOnInit, IOnDestroy {

    public readonly equityTicketVisible$ = new BehaviorSubject(false);
    public readonly multiEquityTicketVisible$ = new BehaviorSubject(false);
    public readonly strategyBuilderVisible$ = new BehaviorSubject(false);
    public readonly ordersEnabled$ = new BehaviorSubject(false);
    public readonly orders$ = new ReplaySubject<FooterOrders>(1);
    public readonly showOrdersLink$ = new BehaviorSubject(false);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       private readonly $element: IRootElementService,
                       private readonly orderViewStore: OrderViewStore,
                       private readonly orderEventStore: OrderEventStore,
                       private readonly equityTicketService: EquityTicketService,
                       private readonly multiAccountEquityTicketService: MultiAccountEquityTicketService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly systemEventService: SystemEventService) {
    }

    public $onInit(): void {
        this.watchEquityTicketVisibility();
        this.watchMultiEquityTicketVisibility();
        this.watchStrategyBuilderVisible();
        this.watchOrdersEnabled();
        this.watchOrderChanges();
        this.watchOrderListSize();
    }

    private watchEquityTicketVisibility() {
        this.currentUserService.current
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((user) => user.ordersEnabled)
            .subscribe((visible) => this.equityTicketVisible$.next(visible));
    }

    private watchMultiEquityTicketVisibility() {
        this.currentUserService.current
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((user) => user.ordersEnabled && user.isAdviser() && user.branding() === Branding.OPEN_MARKETS)
            .subscribe((visible) => this.multiEquityTicketVisible$.next(visible));
    }

    private watchStrategyBuilderVisible() {
        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .map((user) => user.hasFeature(FeatureNames.SCREEN_STRATEGY_BUILDER))
            .subscribe((visible) => this.strategyBuilderVisible$.next(visible));
    }

    private watchOrdersEnabled() {
        this.currentUserService
            .current
            .takeUntil(this.destroyed$)
            .map((user) => user.ordersEnabled)
            .subscribe((enabled) => this.ordersEnabled$.next(enabled));
    }

    public openEquityTicket() {
        this.equityTicketService.openWizard();
    }

    public openMultiEquityTicket() {
        console.log("Open Multi Equity wizard");
        this.multiAccountEquityTicketService.openWizard();
    }

    private watchOrderChanges() {
        this.orderViewStore
            .orders
            .takeUntil(this.destroyed$)
            .map((models) => this.onlyOpenOrders(models))
            .map((models) => this.orderByCreatedDescending(models))
            .map((models) => models.slice(0, 20))
            .map((models) => FooterOrder.from(models))
            .subscribe((orders) => this.orders$.next(orders));
    }

    private onlyOpenOrders(models: OrderViewModels): OrderViewModels {
        return models.filter((m) => m.isOpen);
    }

    private orderByCreatedDescending(models: OrderViewModels): OrderViewModels {
        return [...models].sort((a, b) => b.created.localeCompare(a.created));
    }

    /* Check for the list being larger than the allotted area and show the "All orders" link */
    private watchOrderListSize() {
        const resizeEvents = this.systemEventService.eventsOfType(SystemEvent.VIEW_RESIZE);
        const source$ = Observable.combineLatest(this.orders$, resizeEvents);
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .map(() => this.$element.find(".order-list"))
               .map((jq) => jq[0])
               .filter(isNotNull)
               .subscribe((el) => {
                   // Need to wait for repaint to happen
                   setTimeout(() => {
                       const overflow = el.clientHeight < el.scrollHeight;
                       this.showOrdersLink$.next(overflow);
                   }, 100);
               });
    }

    public isEquity(footerOrder: FooterOrder) {
        const orderModel = footerOrder.model;
        return (orderModel instanceof EquityOrderViewModel) || (orderModel instanceof WarrantOrderViewModel);
    }

    public orderIdentifier(order: FooterOrder): string {
        const model = order.model;
        if (isIressOrderModel(model)) {
            return `IRESS:${model.serverName}:${model.orderNumber}`;
        } else if (isSimulatedOrderModel(model)) {
            return `SIMULATED:${model.id}`;
        } else {
            throw Error(`Unable to make identifier for model type ${typeof model}`);
        }
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
